import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { isJsObject } from '@util/utils';
import { Subject, Observable, Subscription } from 'rxjs';
import { DataService } from '../../data/data.service';

const enum AcValues {
  STANDARD = 'autocomplete-standard',
  EXTENDED = 'autocomplete-extended'
};

@Component({
  selector: 'app-user-autocomplete',
  templateUrl: './user-autocomplete.component.html'
})
export class UserAutocompleteComponent implements OnDestroy, OnInit {

  cls = '';

  itemSize = 56;

  init = false;

  optionCls: string = AcValues.STANDARD;

  @Input() readonly = false;

  public _address = false;
  @Input() set address(addr: boolean) {
    this._address = addr;

    this.itemSize = this._address ? 112 : 56;
    this.optionCls = this._address ? AcValues.EXTENDED : AcValues.STANDARD;
  }

  @Input() name = '';

  @Input() label = '';

  public _required = false;

  @Input() set required(req: boolean) {
    this._required = req;
  }

  public _value: any = {};
  @Input() set value(data: any) {
    if (this._value !== data || (data && this._value?.id !== data.id)) {
      this._value = data;
      this.updateSelected();
    }
  }

  private externalValues = false;
  public _values: any = [];
  @Input() set values(data: any) {
    this.externalValues = true;
    this._values = data;
  }

  @Output() valueChange = new EventEmitter();

  public _url = '';

  @Input() set url(url: string) {
    this._url = url;
    this.getData();
  }

  changeSub!: Subscription;

  inputControl = new UntypedFormControl();

  lastInputText = '';

  sub = new Subject<any>();

  filteredOptions!: Observable<any>;

  displayWith = (data: any) => {
    if (data) {
      if (data.fname || data.lname) {
        return data.fname + ' ' + data.lname + ' (' + data.id + ')';
      }
      return data.first_name + ' ' + data.last_name + ' (' + data.id + ')';
    }
    return '';
  }

  constructor(
    private dataService: DataService,
  ) {
    this.filteredOptions = this.sub.asObservable();
  }

  updateSelected() {
    if (this._value) {
      this.inputControl.setValue(this._value);
    }
  }

  getData() {
    if (!this.externalValues &&
      this.init &&
      this._url) {

      this.dataService.get(this._url).subscribe((data: any) => {
        if (Array.isArray(data?.result?.rows)) {
          this._values = data.result.rows;
          this.updateSelected();
          this.filterOptions(this.lastInputText);
        } else {
          console.warn('Missing ACD:', data);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.changeSub && this.changeSub.unsubscribe()
  }

  ngOnInit(): void {
    this.init = true;
    this.getData();

    this.changeSub = this.inputControl.valueChanges.subscribe((item: any) => {
      if (this._value === item ||
        (item.id && this._value?.id === item.id)) {
        return;
      }

      if (typeof item === 'string') {
        item = item.trim();
        this.lastInputText = item;
        this.filterOptions(item);

        if (!item) {
          this._value = null;
          this.valueChange.emit(this._value);
        }
        return;
      }

      this._value = isJsObject(item) ? item : null;

      this.valueChange.emit(this._value);
    });
  }

  filterOptions(text: string) {
    if (!text) {
      this.sub.next(this._values);
      return;
    }

    const result = this._values.filter((item: any) => {
      if (item?.id) {
        const fname = ((item.fname || item.first_name || '') + '').toLowerCase();
        const lname = ((item.lname || item.last_name || '') + '').toLowerCase();

        return ('' + item.id).indexOf(text) >= 0 ||
          fname.indexOf(text) === 0 ||
          lname.indexOf(text) === 0;
      }
      return false;
    });

    this.sub.next(result);
  }

  highlightError() {
    this.cls = 'error-highlight';

    setTimeout(() => {
      this.cls = '';
    }, 1000);
  }

  setById(id: any): boolean {
    const entry = this._values.find((item: any) => {
      return item?.id && (item.id + '' === '' + id);
    });

    if (entry) {
      this.inputControl.setValue(entry);
      return true;
    }
    return false;
  }

  addEntry(res: any) {
    if (isJsObject(res)) {
      if (!res.fname && res.first_name) {
        res.fname = res.first_name;
        delete res.first_name;
      }

      if (!res.lname && res.last_name) {
        res.lname = res.last_name;
        delete res.last_name;
      }

      this._values.push(res);
    }
  }
}
